import type { ArabicCatalog } from "@ncp/i18n";
import type en from "./decisionControls.en";

/** الضوابط على القرارات: الفحص قبل التوقيع، ووسم عدم قابلية التوقيع، والسند القانوني للقرار في تاريخه، والملفات المفتوحة في مرحلة القرار، واعتماد الإدارة للنماذج وسجل التفويضات والقواعد القانونية. */
const ar: ArabicCatalog<typeof en> = {
  readiness: {
    title: "قبل التوقيع",
    hint: "يفحص مشروع القرار كما هو: اعتماد الصيغة، وكل حكم يستشهد به، والتفويض الساري اليوم، والشروط، ووسم عدم قابلية التوقيع، واتساق القرار مع التحليل المعتمد.",
    checking: "جار فحص المشروع…",
    signable: "لا يوجد ما يحول دون التوقيع.",
    blocked: "لا يمكن توقيع القرار بعد",
    notes: "للعلم قبل التوقيع",
    template: "النموذج",
    templateLine: "{{name}} · الإصدار {{version}}",
    pending: "الإصدار {{version}} بانتظار الاعتماد",
    register: "سجل القواعد القانونية",
    registerLine: "الإصدار {{version}} · تم التحقق من {{verified}} من {{count}} من الأحكام المستشهد بها",
    citations: "الأحكام التي يستشهد بها القرار",
    signatory: "يوقعه",
    signatoryLine: "{{capacity}} بموجب {{authority}}",
    delegations: "سجل التفويضات",
    delegationsLine: "الإصدار {{version}}",
    departureReasons: "أسبابك لمخالفة التحليل",
    departureHint: "تسجل مع القرار وفي سجل الملف، ويبين القرار الموقع النقاط التي يخالف فيها التحليل.",
    status: { Approved: "معتمد", AwaitingApproval: "بانتظار اعتماد الإدارة", EditedSinceApproval: "معدل بعد اعتماده", Verified: "تم التحقق", VerificationRequired: "يتطلب التحقق", NotInRegister: "غير مسجل" },
  },
  hold: {
    mark: "وسم بعدم قابلية التوقيع",
    markTitle: "وسم مستند القرار بأنه غير قابل للتوقيع",
    markHint: "ما دام الوسم قائما لا يوقع القرار ولا يسجل الملف مبتوتا فيه ولا يخطر بالقرار الصادر. ويحفظ السبب واسمك والوقت في سجل الملف.",
    reason: "سبب عدم قابلية التوقيع",
    reasonAr: "سبب عدم قابلية التوقيع (بالعربية)",
    marked: "موسوم بعدم قابلية التوقيع",
    markedBy: "{{by}} · {{date}}",
    lift: "رفع الوسم",
    liftTitle: "رفع وسم عدم قابلية التوقيع",
    liftReason: "سبب قابلية التوقيع الآن",
    markedToast: "وسم مستند القرار بأنه غير قابل للتوقيع",
    liftedToast: "رفع الوسم",
  },
  basis: {
    open: "السند القانوني في تاريخ القرار",
    close: "إخفاء السند القانوني",
    built: "النموذج {{template}}، الإصدار {{version}} · سجل القواعد القانونية {{register}} · سجل التفويضات الإصدار {{delegations}}",
    recorded: "كما سجل مع القرار عند توقيعه.",
    rebuilt: "صدر قبل تسجيل الاستشهادات: أعيد بناؤه من السجل كما كان في {{date}}.",
    gazette: "روجع على: {{reference}}",
    verifiedBy: "تحقق منه {{by}} في {{date}}",
    departures: "مخالفة التحليل وأسباب الموقع",
    word: "تنزيل بصيغة Word",
    none: "لم يسجل أي حكم.",
  },
  open: {
    tab: "مفتوحة في مرحلة القرار",
    hint: "كل ملف في مرحلة القرار لم يصدر فيه قرار، وكل ملف وسم مستند قراره بعدم قابلية التوقيع، مع سبب بقاء كل منها مفتوحا.",
    empty: "لا يوجد ملف ينتظر في مرحلة القرار.",
    since: "في هذه المرحلة منذ {{date}}",
    marked: "موسوم بعدم قابلية التوقيع",
    issued: "صدر القرار {{number}}",
    range: "{{from}}–{{to}} من {{total}}",
    pageOf: "الصفحة {{page}} من {{pages}}",
    previous: "الصفحة السابقة",
    next: "الصفحة التالية",
  },
  letters: {
    title: "يستشهد بأحكام لم تتحقق منها الإدارة بعد",
    hint: "أوقف هذا الخطاب لأنه يستشهد بأحكام لم تراجعها الإدارة بعد مقابل النص العربي في الجريدة الرسمية. يقرأ الخطاب مع الاستشهادات أدناه، ثم يؤكد – وتسجل الأحكام مع الإرسال – أو يلغى. وتسري المدد القانونية كما هي، وتبدأ مدة الطرف من تاريخ التأكيد.",
    sentHint: "أرسل هذا الخطاب مستشهدا بأحكام لم تكن الإدارة قد راجعتها بعد مقابل النص العربي في الجريدة الرسمية، وسجل في سجل الملف تأكيد الموظف المرسل والأحكام المستشهد بها.",
    writtenOut: "يكتب قالبه هذه المواد نصا بدلا من الاستشهاد بها من سجل القواعد القانونية: {{articles}}.",
    draftHint: "قبل الإرسال: يستشهد هذا الخطاب بأحكام لم تراجعها الإدارة بعد مقابل النص العربي في الجريدة الرسمية. وإذا أرسلته يرسل، وتسجل الأحكام أدناه مع الإرسال في سجل الملف.",
    checking: "تقرأ استشهادات الخطاب…",
  },
  method: {
    hint: "تحسب كل الأرقام والدرجات التي تعرضها المنصة بمنهجية الاحتساب المنشورة، في إصدارات. ويبقى الإصدار «مقترحا» إلى أن تعتمده الإدارة. ويسجل هنا خبير قانوني أو المدير أو الوكيل المساعد أو وكيل الوزارة اعتماد الإدارة المكتوب؛ ويدقق، ولا يسري إلا على ذلك الإصدار – فيعود الإصدار التالي الذي ينشره إصدار جديد «مقترحا».",
    version: "منهجية الاحتساب، الإصدار {{version}}",
    approved: "معتمدة",
    proposed: "مقترحة",
    reference: "مرجع الاعتماد المكتوب",
    referenceHint: "كتاب الإدارة أو مذكرتها ورقمها.",
    referenceAr: "المرجع بالعربية (اختياري)",
    approvedOn: "تاريخ الاعتماد المكتوب",
    recordedBy: "سجله",
    note: "ملاحظة (اختياري)",
    notApproved: "الإصدار {{version}} بانتظار اعتماد الإدارة: وإلى أن يسجل يظهر كل رقم محسوب به «مقترحا».",
    earlierApproved: "اعتمد الإصدار {{earlier}} ({{reference}}، {{date}}). والمنهجية السارية الآن هي الإصدار {{version}}، وتبقى «مقترحة» إلى أن تعتمدها الإدارة.",
    pendingRelease: "يعد إصدار جديد الإصدار {{version}} ({{status}}): ويعتمد مستقلا بعد نشره.",
    releaseStatus: { Draft: "مسودة", PendingApproval: "بانتظار مسؤول ثان", Approved: "معتمد ولم ينشر بعد", Scheduled: "مجدول" },
    recordTitle: "تسجيل اعتماد الإدارة للإصدار {{version}}",
    recordHint: "لا يسجل الاعتماد إلا بعد أن تمنحه الإدارة كتابة. ويدقق باسمك ولا يسري إلا على هذا الإصدار.",
    record: "تسجيل اعتماد الإصدار {{version}}",
    recorded: "سجل اعتماد الإدارة",
    whoRecords: "يسجل اعتماد الإدارة: {{roles}}. ولا يسجله مسؤول النظام.",
    measures_zero: "لا مقاييس في المنهجية",
    measures_one: "مقياس واحد في المنهجية",
    measures_two: "مقياسان في المنهجية",
    measures_few: "{{count}} مقاييس في المنهجية",
    measures_many: "{{count}} مقياسا في المنهجية",
    measures_other: "{{count}} مقياس في المنهجية",
  },
  register: {
    tabs: { rules: "القواعد القانونية", templates: "نماذج القرارات", signatories: "الموقعون", glossary: "المسرد", method: "منهجية الاحتساب" },
    glossary: {
      hint: "المصطلحات العربية والإنجليزية المعتمدة، لكل منها تعريفه ومصدره. يقترح الباحث القانوني أو الخبير القانوني أو المدير التعديل في إصدار جديد، ويعتمده خبير قانوني أو المدير من غير معده. وتعمل مقارنة الترجمة ووكلاء الصياغة بالإصدار النافذ.",
      inForce: "النافذ: الإصدار {{version}}",
      filter: "البحث عن مصطلح",
      none: "لا يوجد مصطلح مطابق.",
      source: "المصدر",
      versions: "الإصدارات",
      count: "{{count}} مصطلح",
      count_one: "مصطلح واحد",
      count_two: "مصطلحان",
      count_few: "{{count}} مصطلحات",
      count_many: "{{count}} مصطلحا",
      programList: "قائمة المنصة",
      approve: "اعتماد الإصدار {{version}}",
      approved: "اعتمد إصدار المسرد",
      notYours: "يعتمده موظف آخر",
      propose: "اقتراح تعديل",
      proposeTitle: "اقتراح إصدار جديد من المسرد",
      proposeHint: "يمكن تعديل المصطلحات أو إضافتها أو حذفها. ينتظر الإصدار الجديد اعتماد خبير قانوني أو المدير من غير معده، ويبقى الإصدار النافذ معمولا به إلى حين اعتماده.",
      termEn: "المصطلح بالإنجليزية",
      termAr: "المصطلح بالعربية",
      definitionEn: "التعريف بالإنجليزية",
      definitionAr: "التعريف بالعربية",
      sourceEn: "المصدر بالإنجليزية",
      sourceAr: "المصدر بالعربية",
      alsoEn: "صيغ إنجليزية أخرى (يفصل بينها بفاصلة)",
      alsoAr: "صيغ عربية أخرى (يفصل بينها بفاصلة)",
      remove: "حذف هذا المصطلح",
      add: "إضافة مصطلح",
      note: "ما الذي يتغير ولماذا",
      submit: "الإرسال للاعتماد",
      drafted: "أرسل الإصدار الجديد للاعتماد",
    },
    rules: {
      hint: "كل قاعدة تطبقها المنصة وتستشهد بها قراراتها وخطاباتها وإخطاراتها ومددها وخطوات إجراءاتها، بمصدرها وتاريخ نفاذها وموقف تحقق الإدارة منها. وما دام الحكم لم يتحقق منه مقابل النص العربي في الجريدة الرسمية: لا يوقع قرار يستشهد به، ويوقف الخطاب أو الإخطار الذي يستشهد به إلى أن يقرأه موظف إذا لم يرسله موظف، ويسجل الحكم في سجل الملف إذا أرسله موظف، وتستمر المدة في السريان وفق القاعدة القانونية مع بيان «لم تتحقق منه الإدارة بعد».",
      version: "إصدار السجل {{version}}",
      filter: "البحث عن حكم",
      asOf: "السجل كما كان في",
      cited: "صيغة الاستشهاد",
      effective: "نافذ من",
      verify: "تسجيل التحقق",
      verifyTitle: "التحقق مقابل الجريدة الرسمية",
      gazette: "عدد الجريدة الرسمية وتاريخه والصفحة التي روجع عليها",
      note: "ملاحظة",
      status: "الحالة",
      saved: "سجل التحقق",
      history: "سجل التحقق",
      historyLine: "{{by}} · {{date}} · {{status}}",
    },
    templates: {
      hint: "صيغة كل قرار في إصدارات. تعدل الصيغة في استوديو سير العمل إصدارا جديدا، ويعتمدها هنا خبير قانوني أو المدير غير الذي أعدها. ولا يصدر قرار قابل للتوقيع إلا من إصدار معتمد لم يعدل بعد اعتماده، ويبين كل قرار مطبوع الإصدار المستخدم.",
      procedure: { EconomicConcentration: "التركز الاقتصادي", Complaint: "الشكاوى", Whistleblowing: "التحقيقات والإنفاذ", Exemption: "طلبات الاستثناء", ExemptionAmendment: "تعديل الاستثناء", Appeal: "التظلمات" },
      inForce: "الساري: الإصدار {{version}}",
      versions: "الإصدارات",
      versionLine: "الإصدار {{version}} · {{status}}",
      draftedBy: "أعده {{by}} في {{date}}",
      programWording: "صيغة المنصة",
      approvedBy: "اعتمده {{by}} في {{date}}، ساري من {{from}}",
      approve: "اعتماد الإصدار {{version}}",
      effectiveFrom: "ساري من",
      approved: "اعتمد الإصدار",
      block: "الجزء",
      wording: "صيغة الإصدار {{version}}",
    },
    signatories: {
      hint: "من يوقع كل قرار وبأي صفة وبموجب أي تفويض، مع تواريخ سريان كل منها، بما فيها التكليف بالإنابة. تعد التعديلات في استوديو سير العمل إصدارا جديدا وتعتمد هنا، ولا يوقع قرار إلا بموجب إصدار معتمد ومن الموظف الذي يسميه تفويض ساري.",
      inForce: "إصدار السجل {{version}}",
      capacity: "الصفة",
      name: "الاسم",
      authority: "السند",
      decisions: "القرارات",
      period: "السريان",
      acting: "بالإنابة",
      fromTo: "{{from}} – {{to}}",
      open: "دون تاريخ انتهاء",
      approve: "اعتماد الإصدار {{version}}",
      approved: "اعتمد سجل التفويضات",
      versions: "الإصدارات",
      unnamed: "الاسم من حساب الموقع",
    },
  },
  testEnvironment: {
    line: "بيئة الاختبار – ضوابط القرار مخففة، وليس هذا سلوك منصة الإنتاج:",
    codes: {
      Registers: "تنبه السجلات بدلا من منع التوقيع",
      Heads: "يتولى المدير ومن فوقه كل صلاحية ويوقعون كل قرار",
      Penalty: "خطوات الجزاء مفتوحة قبل تأكيد الأساس",
      Letters: "لا يوقف الخطاب الذي يستشهد بأحكام غير متحقق منها لهذا السبب وحده",
    },
    outstanding: "اعتمادات للإدارة لم تصدر بعد (بيئة الاختبار)",
    acknowledge: "أوقع وأنا أعلم أن هذه الاعتمادات لم تصدر بعد. وسيذكر القرار وسجل الملف ونسختا PDF وWord أنه وقع وفق ضوابط بيئة الاختبار.",
    acknowledgeNeeded: "يلزم وضع علامة الإقرار للتوقيع مع بقاء هذه الاعتمادات معلقة.",
    signed: "وقع وفق ضوابط القرار الخاصة ببيئة الاختبار",
    signedOutstanding: "المعلق عند التوقيع: {{list}}",
    acknowledgedBy: "أقر بذلك {{name}}",
  },
};

export default ar;
